import { useEffect, useMemo, useState } from 'react';
import { trackAdEvent } from '@/lib/adPixels';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Truck, Zap, Package, ShoppingBag, Loader2, Store, AlertCircle, UserPlus } from 'lucide-react';
import { useVatRate } from '@/hooks/useVatRate';
import { StateCitySelect } from '@/components/shared/StateCitySelect';
import { isValidStateCity } from '@/lib/nigeriaLocations';
import { useDeliveryRates } from '@/hooks/useDeliveryRates';
import { getFezRateDirect } from '@/services/deliveryRates';
import { supabase } from '@/integrations/supabase/client';
import { PaymentMethodSelector } from '@/components/checkout/PaymentMethodSelector';
import type { PaymentMethod } from '@/services/payOnDelivery';

interface DeliveryRate {
  id: string;
  name: string;
  type: 'regular' | 'express';
  price: number;
  estimatedDays: string;
  provider?: 'fez' | 'self';
  isFallback?: boolean;
}

// No more static ₦2,500 fallback — we fetch dynamically from get-fez-rate

interface BuyNowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    id: string;
    title: string;
    price: number;
    images?: string[];
    seller_id: string;
  };
  sellerState?: string;
  onConfirm: (deliveryData: {
    deliveryState: string;
    deliveryCity: string;
    deliveryAddress: string;
    recipientName: string;
    recipientPhone: string;
    deliveryFee: number;
    deliveryType: string;
    buyerEmail?: string;
    buyerPassword?: string;
    paymentMethod?: PaymentMethod;
  }) => void;
  isProcessing: boolean;
}

export function BuyNowModal({
  open,
  onOpenChange,
  product,
  sellerState = 'Lagos',
  onConfirm,
  isProcessing
}: BuyNowModalProps) {
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [selectedRateId, setSelectedRateId] = useState<string>('');
  const [sellerDeliveryPref, setSellerDeliveryPref] = useState<string>('partner');
  const [fezFallbackRate, setFezFallbackRate] = useState<DeliveryRate | null>(null);
  const [loadingFezFallback, setLoadingFezFallback] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPassword, setBuyerPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('online');

  useEffect(() => {
    if (open) trackAdEvent('InitiateCheckout', { value: product.price, content_ids: [product.id], num_items: 1 });
  }, [open]);

  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
      if (user?.email) setBuyerEmail(user.email);
    };
    checkAuth();
  }, []);
  
  const { vatRate, vatEnabled } = useVatRate();

  // Check seller delivery preference
  useEffect(() => {
    if (!product.seller_id) return;
    const checkSeller = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('delivery_preference, pickup_address_street, pickup_address_city, pickup_address_state, pickup_address_phone')
          .eq('id', product.seller_id)
          .maybeSingle();

        if (profile) {
          setSellerDeliveryPref(profile.delivery_preference || 'partner');
          console.log('[BuyNowModal] Seller profile:', {
            sellerId: product.seller_id,
            deliveryPreference: profile.delivery_preference,
            pickupStreet: profile.pickup_address_street ? '✓' : '✗',
            pickupCity: profile.pickup_address_city ? '✓' : '✗',
            pickupState: profile.pickup_address_state ? '✓' : '✗',
            pickupPhone: profile.pickup_address_phone ? '✓' : '✗',
          });
        } else {
          console.log('[BuyNowModal] No seller profile found for:', product.seller_id);
        }
      } catch {
        // fallback
      }
    };
    checkSeller();
  }, [product.seller_id]);

  const isFezSeller = sellerDeliveryPref === 'partner';

  const isValidLocation = isValidStateCity(deliveryState, deliveryCity);
  const items = useMemo(() => ([{ quantity: 1, price: product.price, title: product.title }]), [product.price, product.title]);

  const destinationAddress = useMemo(() => {
    const base = [deliveryAddress, deliveryCity, deliveryState].filter(Boolean).join(', ');
    return base || `${deliveryCity}, ${deliveryState}`;
  }, [deliveryAddress, deliveryCity, deliveryState]);

  const { data, isLoading: loadingRates, isError: isRatesError, error: ratesError } = useDeliveryRates({
    sellerId: product.seller_id,
    destinationState: deliveryState,
    destinationCity: deliveryCity,
    destinationAddress,
    items,
  });

  // Fetch dynamic Fez rate when API returns 0 rates
  useEffect(() => {
    if (!isFezSeller || !isValidLocation || loadingRates) return;
    
    const apiRates = data?.rates || [];
    if (apiRates.length > 0) {
      // Got rates from the main API, clear any fallback
      setFezFallbackRate(null);
      return;
    }

    // No rates from main API — fetch from get-fez-rate
    setLoadingFezFallback(true);
    console.log('[BuyNowModal] Main API returned 0 rates, fetching from get-fez-rate...');
    
    getFezRateDirect({
      sellerId: product.seller_id,
      recipientState: deliveryState,
      recipientAddress: `${deliveryAddress}, ${deliveryCity}, ${deliveryState}`,
      weight: 1,
      valueOfItem: product.price,
    }).then((result) => {
      console.log('[BuyNowModal] get-fez-rate result:', result);
      if (result.success && result.delivery_fee > 0) {
        setFezFallbackRate({
          id: 'fez-dynamic',
          name: 'Fez Delivery',
          type: 'regular',
          price: result.delivery_fee,
          estimatedDays: '3-5 business days',
          provider: 'fez',
          isFallback: false,
        });
      } else {
        console.error('[BuyNowModal] get-fez-rate failed, no fallback available');
        setFezFallbackRate(null);
      }
    }).catch((err) => {
      console.error('[BuyNowModal] get-fez-rate error:', err);
      setFezFallbackRate(null);
    }).finally(() => {
      setLoadingFezFallback(false);
    });
  }, [isFezSeller, isValidLocation, loadingRates, data?.rates, deliveryState, deliveryCity, deliveryAddress, product.seller_id, product.price]);

  const deliveryRates: DeliveryRate[] = useMemo(() => {
    const raw = data?.rates || [];
    const mapped = raw.map((r) => ({
      id: r.id,
      name: r.name,
      type: r.type,
      price: r.price,
      estimatedDays: r.estimatedDays,
      provider: (r.provider === 'self-delivery' ? 'self' : 'fez') as 'fez' | 'self',
    }));

    console.log('[BuyNowModal] API rates:', mapped.length, 'isFezSeller:', isFezSeller, 'isError:', isRatesError, 'isLoading:', loadingRates, 'fezFallback:', fezFallbackRate?.price);

    // If we got rates from main API, use them
    if (mapped.length > 0) return mapped;

    // Use dynamic fallback from get-fez-rate
    if (fezFallbackRate && isValidLocation && !loadingRates) {
      console.log('[BuyNowModal] Using dynamic Fez rate: ₦' + fezFallbackRate.price);
      return [fezFallbackRate];
    }

    return [];
  }, [data?.rates, isFezSeller, isValidLocation, loadingRates, isRatesError, fezFallbackRate]);

  useEffect(() => {
    // When location becomes invalid (e.g., state changed), clear selection and prevent stale totals.
    if (!isValidLocation) {
      setSelectedRateId('');
      return;
    }

    if (deliveryRates.length === 0) {
      setSelectedRateId('');
      return;
    }

    const stillExists = deliveryRates.some((r) => r.id === selectedRateId);
    if (!stillExists) {
      setSelectedRateId(deliveryRates[0].id);
    }
  }, [isValidLocation, deliveryRates, selectedRateId]);

  const selectedRate = useMemo(() => {
    if (!selectedRateId) return null;
    return deliveryRates.find((r) => r.id === selectedRateId) || null;
  }, [deliveryRates, selectedRateId]);

  const subtotal = product.price;
  const deliveryFee = selectedRate?.price || 0;
  const vat = vatEnabled ? Math.round(subtotal * (vatRate / 100)) : 0; // VAT on subtotal only
  const isPayOnDelivery = paymentMethod === 'pay_on_delivery';
  const transactionFee = isPayOnDelivery ? 0 : Math.round((subtotal + deliveryFee + vat) * 0.015); // 1.5% online transaction fee
  const total = subtotal + deliveryFee + vat + transactionFee;

  const isFormValid = isValidLocation && deliveryAddress && recipientName && recipientPhone && selectedRate && 
    (isLoggedIn || isPayOnDelivery || (buyerEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail) && buyerPassword && buyerPassword.length >= 6));

  const handleConfirm = () => {
    if (!isFormValid || !selectedRate) return;
    
    // Store credentials for account creation after payment
    if (!isPayOnDelivery && !isLoggedIn && buyerEmail && buyerPassword) {
      sessionStorage.setItem('pending_buyer_email', buyerEmail.trim());
      sessionStorage.setItem('pending_buyer_password', buyerPassword);
      sessionStorage.setItem('pending_buyer_name', recipientName.trim());
      sessionStorage.setItem('pending_buyer_phone', recipientPhone.trim());
    }
    
    onConfirm({
      deliveryState,
      deliveryCity,
      deliveryAddress,
      recipientName,
      recipientPhone,
      deliveryFee: selectedRate.price,
      deliveryType: selectedRate.provider === 'fez' ? 'fez' : 'self',
      buyerEmail: buyerEmail.trim() || undefined,
      buyerPassword: buyerPassword || undefined,
      paymentMethod,
    });
  };

  const getIcon = (type: string, provider?: string) => {
    if (provider === 'self') return <Store className="h-4 w-4 text-primary" />;
    return type === 'express' 
      ? <Zap className="h-4 w-4 text-amber-500" />
      : <Truck className="h-4 w-4 text-primary" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-3 sm:p-6 rounded-lg sm:rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Complete Your Purchase
          </DialogTitle>
          <DialogDescription>
            Enter your delivery details to proceed with payment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
          {/* Product Summary */}
          <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-muted rounded-lg">
            {product.images?.[0] ? (
              <img src={product.images[0]} alt={product.title} className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-background rounded flex items-center justify-center flex-shrink-0">
                <Package className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm sm:text-base truncate">{product.title}</p>
              <p className="text-base sm:text-lg font-bold text-primary">₦{product.price.toLocaleString()}</p>
            </div>
          </div>

          <Separator />

          {/* Recipient Details */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-medium text-sm">Recipient Details</h4>
            <div className="grid grid-cols-1 gap-2 sm:gap-3">
              <div>
                <Label htmlFor="recipientName" className="text-xs sm:text-sm">Full Name *</Label>
                <Input
                  id="recipientName"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Enter recipient name"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="recipientPhone" className="text-xs sm:text-sm">Phone Number *</Label>
                <Input
                  id="recipientPhone"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="e.g., 08012345678"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Account Creation - Non-logged-in users */}
          {!isLoggedIn && (
            <div className="space-y-2 sm:space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Create Your Account
              </h4>
              <p className="text-[11px] text-muted-foreground">
                Sign up to track your order and manage purchases
              </p>
              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="buyerEmailModal" className="text-xs sm:text-sm">Email Address *</Label>
                  <Input
                    id="buyerEmailModal"
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="buyerPasswordModal" className="text-xs sm:text-sm">Password *</Label>
                  <Input
                    id="buyerPasswordModal"
                    type="password"
                    value={buyerPassword}
                    onChange={(e) => setBuyerPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Delivery Address */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-medium text-sm">Delivery Address</h4>
            <div className="grid gap-2 sm:gap-3">
              <StateCitySelect
                stateValue={deliveryState}
                cityValue={deliveryCity}
                onStateChange={setDeliveryState}
                onCityChange={setDeliveryCity}
              />
              <div>
                <Label htmlFor="address" className="text-xs sm:text-sm">Street Address *</Label>
                <Input
                  id="address"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Enter full address"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Delivery Options */}
          {deliveryState && (
            <div className="space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Delivery Options
              </h4>

              {!isValidLocation && (
                <p className="text-sm text-muted-foreground">
                  Select a valid <span className="font-medium">State</span> and <span className="font-medium">City</span> to calculate delivery.
                </p>
              )}

              {isValidLocation && isRatesError && deliveryRates.length > 0 && deliveryRates[0]?.isFallback && (
                <div className="flex items-center gap-2 text-amber-600 text-xs p-2 bg-amber-50 rounded">
                  <AlertCircle className="h-3 w-3 flex-shrink-0" />
                  <span>Using estimated pricing. Final fee may vary.</span>
                </div>
              )}
              
              {isValidLocation && (loadingRates || loadingFezFallback) ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : isValidLocation && deliveryRates.length > 0 ? (
                <RadioGroup
                  value={selectedRateId || ''}
                  onValueChange={(value) => {
                    setSelectedRateId(value);
                  }}
                  className="space-y-2"
                >
                  {deliveryRates.map((rate) => (
                    <Label
                      key={rate.id}
                      htmlFor={rate.id}
                      className={`flex items-center justify-between p-2.5 sm:p-3 border rounded-lg cursor-pointer transition-all text-sm sm:text-base ${
                        selectedRateId === rate.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value={rate.id} id={rate.id} />
                        {getIcon(rate.type, rate.provider)}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{rate.name}</span>
                            {rate.type === 'express' && (
                              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                                Fastest
                              </Badge>
                            )}
                            {rate.isFallback && (
                              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">
                                Estimated
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{rate.estimatedDays}</p>
                        </div>
                      </div>
                      <span className="font-bold">₦{rate.price.toLocaleString()}</span>
                    </Label>
                  ))}
                </RadioGroup>
              ) : isValidLocation && !loadingRates && !loadingFezFallback ? (
                <div className="text-center py-3 text-muted-foreground text-sm">
                  <Package className="h-6 w-6 mx-auto mb-1 opacity-50" />
                  <p>No delivery options available for this location</p>
                </div>
              ) : null}
            </div>
          )}

          <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />

          <Separator />

          {/* Order Summary */}
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>₦{subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Delivery</span>
              <span>{deliveryFee > 0 ? `₦${deliveryFee.toLocaleString()}` : '-'}</span>
            </div>
            {vatEnabled && (
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-muted-foreground">VAT ({vatRate}%)</span>
                <span>₦{vat.toLocaleString()}</span>
              </div>
            )}
            {!isPayOnDelivery && (
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-muted-foreground">Transaction Fee (1.5%)</span>
                <span>₦{transactionFee.toLocaleString()}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-bold text-base sm:text-lg">
              <span>Total</span>
              <span className="text-primary">₦{total.toLocaleString()}</span>
            </div>
          </div>

          {/* Fez validation warning */}
          {selectedRate?.provider === 'fez' && (!recipientName || !recipientPhone || !deliveryAddress || !isValidLocation) && (
            <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg border border-destructive/20">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Please complete delivery details for Fez shipping</span>
            </div>
          )}

          {/* Confirm Button */}
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[BuyNowModal] Pay button clicked', { isFormValid, isProcessing, selectedRate });
              handleConfirm();
            }} 
            disabled={!isFormValid || isProcessing}
            className="w-full relative z-50"
            size="lg"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : !recipientName || !recipientPhone ? (
              'Enter Recipient Details'
            ) : !isValidLocation ? (
              'Select Delivery Location'
            ) : !deliveryAddress ? (
              'Enter Street Address'
            ) : !selectedRate ? (
              'Select Delivery Option'
            ) : (
              <>
                <ShoppingBag className="h-4 w-4 mr-2" />
                {isPayOnDelivery ? `Place Order • Pay ₦${total.toLocaleString()} on Delivery` : `Pay ₦${total.toLocaleString()}`}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
